import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { Content } from '../../../types/content';
import { LoadingSpinner } from '../../common';
import VideoThumbnail from '../../video/VideoThumbnail';
import AudioThumbnail from '../../audio/AudioThumbnail';
import ShortsThumbnail from '../../shorts/ShortsThumbnail';

export interface MediaListContainerProps {
  title?: string;
  items: Content[];
  loading?: boolean;
  onLoadMore?: () => void;
  className?: string;
  type?: 'video' | 'audio' | 'shorts';
  hideLoadMore?: boolean;
}

const MediaListContainer: React.FC<MediaListContainerProps> = ({
  title,
  items = [],
  loading = false,
  onLoadMore,
  className = '',
  type = 'video',
  hideLoadMore = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();

  const renderThumbnail = (item: Content) => {
    switch (type) {
      case 'audio':
        return <AudioThumbnail {...item} />;
      case 'shorts':
        return <ShortsThumbnail {...item} />;
      default:
        return <VideoThumbnail {...item} />;
    }
  };

  if (!items.length && !loading) {
    return null;
  }

  return (
    <div className={`${theme.mode === "dark" ? "bg-surface-secondary" : "bg-background-secondary"} sm:rounded-xl border border-border-primary overflow-hidden ${className}`}>
      {title && (
        <div className="p-4 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">
              {title}
            </h3>
          </div>
        </div>
      )}
      <div className="divide-y divide-border-primary">
        {loading && items.length === 0 ? (
          <div className="flex justify-center items-center p-8">
            <LoadingSpinner size="md" />
          </div>
        ) : (
          <>
            {items.map((item) => (
              <div key={item.id} className="p-3 hover:bg-surface-primary transition-colors">
                {renderThumbnail(item)}
              </div>
            ))}
            {loading && (
              <div className="flex justify-center items-center p-4">
                <LoadingSpinner size="sm" />
              </div>
            )}
            {!hideLoadMore && items.length > 0 && !loading && onLoadMore && (
              <button
                onClick={onLoadMore}
                className="w-full p-3 text-center text-primary-500 hover:bg-surface-primary transition-colors text-sm font-medium"
              >
                {t('common.seeMore')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MediaListContainer; 